import { createContext, useContext, useMemo, useReducer, type ReactNode } from "react";

import type {
  DispositionView,
  EmergencyResponse,
  PatientAnswer,
  PatientDraftState,
  QuestionView,
  SemanticInterpretationView,
  CaseResponseEnvelope,
  PatientSnapshot,
} from "../contracts/ui";

export const initialPatientState: PatientDraftState = {
  caseId: "",
  caseVersion: 0,
  knowledgeVersion: "2026.08.29",
  requestId: "",
  subjectId: null,
  subjectLabel: "You",
  answers: {},
  interpretations: [],
  disposition: null,
  pendingQuestion: null,
  emergency: null,
  saveStatus: "not_saved",
  stale: false,
  dependentAnswersPending: 0,
};

export type PatientAction =
  | { type: "reset" }
  | { type: "hydrate"; envelope: CaseResponseEnvelope; snapshot: PatientSnapshot }
  | { type: "subject_selected"; subjectId: "self" | "other" }
  | { type: "saving" }
  | { type: "answer_saved"; answer: PatientAnswer; caseVersion: number }
  | { type: "question_received"; question: QuestionView; caseVersion: number }
  | { type: "interpretations_received"; interpretations: SemanticInterpretationView[]; caseVersion: number }
  | { type: "interpretation_changed"; interpretationId: string; state: "confirmed" | "rejected" }
  | { type: "emergency_received"; emergency: EmergencyResponse["emergency"]; caseVersion: number }
  | { type: "emergency_corrected" }
  | { type: "disposition_received"; disposition: DispositionView; caseVersion: number }
  | { type: "dependent_answers_invalidated"; count: number }
  | { type: "network_failed" }
  | { type: "stale" };

export function patientReducer(state: PatientDraftState, action: PatientAction): PatientDraftState {
  switch (action.type) {
    case "reset": return initialPatientState;
    case "hydrate": return {
      ...state, caseId: action.envelope.case_id, caseVersion: action.envelope.case_version,
      requestId: action.envelope.request_id, knowledgeVersion: action.envelope.knowledge_version,
      subjectId: action.snapshot.subject_id, subjectLabel: action.snapshot.subject_label,
      answers: action.snapshot.answers, interpretations: action.snapshot.interpretations,
      pendingQuestion: action.snapshot.pending_question, emergency: action.snapshot.emergency,
      disposition: action.snapshot.disposition, saveStatus: "saved", stale: false,
      dependentAnswersPending: action.snapshot.dependent_answers_pending,
    };
    case "subject_selected":
      return {
        ...state,
        subjectId: action.subjectId,
        subjectLabel: action.subjectId === "self" ? "You" : "The person you are helping",
      };
    case "saving":
      return { ...state, saveStatus: "saving" };
    case "answer_saved":
      return {
        ...state,
        answers: { ...state.answers, [action.answer.question_id]: action.answer },
        caseVersion: action.caseVersion,
        saveStatus: "saved",
      };
    case "question_received":
      return { ...state, pendingQuestion: action.question, caseVersion: action.caseVersion, emergency: null };
    case "interpretations_received":
      return { ...state, interpretations: action.interpretations, caseVersion: action.caseVersion };
    case "interpretation_changed":
      return {
        ...state,
        interpretations: state.interpretations.map((item) =>
          item.interpretation_id === action.interpretationId
            ? { ...item, confirmation_state: action.state }
            : item,
        ),
      };
    case "emergency_received":
      return { ...state, emergency: action.emergency, caseVersion: action.caseVersion, saveStatus: "saved" };
    case "emergency_corrected":
      return { ...state, emergency: null };
    case "disposition_received":
      return { ...state, disposition: action.disposition, caseVersion: action.caseVersion, pendingQuestion: null };
    case "dependent_answers_invalidated":
      return { ...state, dependentAnswersPending: action.count };
    case "network_failed":
      return { ...state, saveStatus: "not_saved" };
    case "stale":
      return { ...state, stale: true };
  }
}

interface CaseContextValue {
  state: PatientDraftState;
  dispatch: React.Dispatch<PatientAction>;
}

const CaseContext = createContext<CaseContextValue | null>(null);

export function CaseStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(patientReducer, initialPatientState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <CaseContext.Provider value={value}>{children}</CaseContext.Provider>;
}

export function useCaseState(): CaseContextValue {
  const value = useContext(CaseContext);
  if (value === null) throw new Error("useCaseState must be used within CaseStateProvider.");
  return value;
}
